import { useEffect, useMemo, useState } from "react";
import { Pencil, Trash2, FolderCog, Search, X, CircleAlert } from "lucide-react";
import Sidebar from "@/components/sidebar.jsx";
import MobileMenuButton from "@/components/ui/mobile-menu-button.jsx";
import PageHeader from "@/components/page-header.jsx";
import PageHeaderActions from "@/components/page-header-actions.jsx";
import { Button } from "@/components/ui/button.jsx";
import { Input } from "@/components/ui/input.jsx";
import { Label } from "@/components/ui/label.jsx";
import { Checkbox } from "@/components/ui/checkbox.jsx";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card.jsx";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table.jsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.jsx";
import {
  createTagGroup,
  deleteTagGroup,
  getTagGroupsForTenant,
  getUniversalDescriptorTagCounts,
  updateTagGroup,
} from "@/api/tags-api.js";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip.jsx";
import { LoadingSkeleton } from "@/components/ui/loading.jsx";
import { useSessionTenant } from "@/contexts/SessionTenantContext.jsx";
import { cn } from "@/lib/utils.js";
import { cardStyles, outlineIconButton, searchStyles, steelChromeButton } from "@/lib/status-styles.js";

/** Matches fleet overview / fleet table card shell */
const fleetOverviewCardClass = cardStyles.panel;

const PERMISSION_ERROR =
  "You don't have permission to manage tag groups for the current tenant.";

const createDefaultFormState = () => ({
  groupId: null,
  groupName: "",
  description: "",
  descriptorIds: [],
});

/**
 * Manage tag groups page
 * @returns {JSX.Element} Manage groups page
 */
export default function ManageGroups() {
  const {
    currentTenantId,
    currentTenant,
    canManageTagGroups: canManageSelectedTenant,
    isLoadingManageTagGroupTenants: isLoadingManageableTenants,
  } = useSessionTenant();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [formState, setFormState] = useState(createDefaultFormState);
  const [isSaving, setIsSaving] = useState(false);
  const [groups, setGroups] = useState([]);
  const [isLoadingGroups, setIsLoadingGroups] = useState(true);
  const [descriptorCounts, setDescriptorCounts] = useState([]);
  const [isLoadingDescriptorCounts, setIsLoadingDescriptorCounts] =
    useState(true);
  const [descriptorSearchTerm, setDescriptorSearchTerm] = useState("");
  const [groupSearchTerm, setGroupSearchTerm] = useState("");
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [pendingDeleteGroup, setPendingDeleteGroup] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const loadDescriptorTagCounts = async () => {
      setIsLoadingDescriptorCounts(true);
      try {
        const response = await getUniversalDescriptorTagCounts();

        if (!isMounted) {
          return;
        }

        setDescriptorCounts(Array.isArray(response) ? response : []);
      } catch (error) {
        console.error(
          "Failed to fetch universal descriptor tag counts:",
          error
        );
        if (isMounted) {
          setDescriptorCounts([]);
        }
      } finally {
        if (isMounted) {
          setIsLoadingDescriptorCounts(false);
        }
      }
    };

    loadDescriptorTagCounts();

    return () => {
      isMounted = false;
    };
  }, [currentTenantId]);

  useEffect(() => {
    let isMounted = true;

    const loadGroupsForTenant = async () => {
      if (!canManageSelectedTenant) {
        setGroups([]);
        setIsLoadingGroups(false);
        return;
      }

      setIsLoadingGroups(true);
      try {
        const response = await getTagGroupsForTenant();
        if (isMounted) {
          setGroups(Array.isArray(response) ? response : []);
        }
      } catch (error) {
        console.error("Failed to fetch tag groups for tenant:", error);
        if (isMounted) {
          setGroups([]);
        }
      } finally {
        if (isMounted) {
          setIsLoadingGroups(false);
        }
      }
    };

    loadGroupsForTenant();

    return () => {
      isMounted = false;
    };
  }, [canManageSelectedTenant, currentTenantId]);

  const selectedTenantDescriptorCounts = useMemo(
    () => (canManageSelectedTenant ? descriptorCounts : []),
    [descriptorCounts, canManageSelectedTenant]
  );

  const filteredDescriptorRows = useMemo(() => {
    const search = descriptorSearchTerm.trim().toLowerCase();

    if (!search) {
      return selectedTenantDescriptorCounts;
    }

    return selectedTenantDescriptorCounts.filter((descriptor) => {
      const tagName = (descriptor.TagName || "").toLowerCase();
      const universalDescriptor = (descriptor.UD || "").toLowerCase();
      return tagName.includes(search) || universalDescriptor.includes(search);
    });
  }, [descriptorSearchTerm, selectedTenantDescriptorCounts]);

  const filteredSortedGroups = useMemo(() => {
    const sorted = [...groups].sort((a, b) =>
      (a.TagGroupName || "").localeCompare(b.TagGroupName || "")
    );

    const search = groupSearchTerm.trim().toLowerCase();
    if (!search) {
      return sorted;
    }

    return sorted.filter((group) => {
      const name = (group.TagGroupName || "").toLowerCase();
      const description = (group.TagDescription || "").toLowerCase();
      return name.includes(search) || description.includes(search);
    });
  }, [groups, groupSearchTerm]);

  const isDuplicateGroupName = useMemo(() => {
    const trimmed = formState.groupName.trim().toLowerCase();
    if (!trimmed) return false;
    return groups.some(
      (g) =>
        (g.TagGroupName || "").trim().toLowerCase() === trimmed
        && Number(g.TagGroupId) !== formState.groupId
    );
  }, [formState.groupName, formState.groupId, groups]);

  /**
   * Toggle descriptor in form selection
   * @param {number} universalDescriptorId - Descriptor ID
   */
  const toggleDescriptorSelection = (universalDescriptorId) => {
    setFormState((previous) => {
      const currentIds = Array.isArray(previous.descriptorIds)
        ? previous.descriptorIds
        : [];

      const hasDescriptor = currentIds.includes(universalDescriptorId);
      const descriptorIds = hasDescriptor
        ? currentIds.filter((id) => id !== universalDescriptorId)
        : [...currentIds, universalDescriptorId];

      return {
        ...previous,
        descriptorIds,
      };
    });
  };

  /**
   * Handle selecting an existing group for editing
   * @param {Object} group - Tag group
   */
  const handleEdit = (group) => {
    const descriptorIds = Array.isArray(group.UniversalDescriptors)
      ? group.UniversalDescriptors.map((descriptor) =>
          Number(descriptor.UniversalDescriptorId)
        ).filter((id) => !Number.isNaN(id))
      : [];

    setFormState({
      groupId: Number(group.TagGroupId),
      groupName: group.TagGroupName || "",
      description: group.TagDescription || "",
      descriptorIds,
    });
  };

  const resetForm = () => setFormState(createDefaultFormState());

  // Reset form + group search whenever the active tenant changes.
  useEffect(() => {
    resetForm();
    setGroupSearchTerm("");
  }, [currentTenantId]);

  /** Defensive guard; the form is also disabled when the user cannot manage. */
  const ensureCanManage = () => {
    if (!canManageSelectedTenant) {
      alert(PERMISSION_ERROR);
      return false;
    }
    return true;
  };

  /**
   * Save create/update action
   * @returns {Promise<void>} Async completion
   */
  const handleSave = async () => {
    if (!ensureCanManage()) return;

    const trimmedName = formState.groupName.trim();
    if (!trimmedName) {
      alert("Group name is required.");
      return;
    }
    if (formState.descriptorIds.length === 0) {
      alert("Select at least one Universal Descriptor.");
      return;
    }

    setIsSaving(true);
    try {
      if (formState.groupId) {
        await updateTagGroup(
          formState.groupId,
          trimmedName,
          formState.description.trim(),
          formState.descriptorIds
        );
      } else {
        await createTagGroup(
          trimmedName,
          formState.description.trim(),
          formState.descriptorIds
        );
      }

      const refreshedGroups = await getTagGroupsForTenant();
      setGroups(Array.isArray(refreshedGroups) ? refreshedGroups : []);
      resetForm();
    } catch (error) {
      console.error("Failed to save group:", error);
      alert("Failed to save group. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteCurrent = () => {
    if (!formState.groupId || !ensureCanManage()) return;
    setPendingDeleteGroup({
      groupId: formState.groupId,
      groupName: formState.groupName || "this group",
    });
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteFromList = (groupId, groupName) => {
    if (!ensureCanManage()) return;
    setPendingDeleteGroup({ groupId, groupName });
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!pendingDeleteGroup?.groupId || !ensureCanManage()) return;

    setIsSaving(true);
    try {
      await deleteTagGroup(pendingDeleteGroup.groupId);
      const refreshedGroups = await getTagGroupsForTenant();
      setGroups(Array.isArray(refreshedGroups) ? refreshedGroups : []);
      if (formState.groupId === pendingDeleteGroup.groupId) {
        resetForm();
      }
      setIsDeleteDialogOpen(false);
      setPendingDeleteGroup(null);
    } catch (error) {
      console.error("Failed to delete group:", error);
      alert("Failed to delete group. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className='flex h-screen bg-background relative'>
      <Dialog
        open={isDeleteDialogOpen}
        onOpenChange={(open) => {
          setIsDeleteDialogOpen(open);
          if (!open) {
            setPendingDeleteGroup(null);
          }
        }}>
        <DialogContent className='sm:max-w-md'>
          <DialogHeader>
            <DialogTitle>Delete Tag Group</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete{" "}
              <span className='font-medium'>
                "{pendingDeleteGroup?.groupName || "this group"}"
              </span>
              ? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className='flex flex-row justify-end gap-2'>
            <Button
              variant='outline'
              onClick={() => {
                setIsDeleteDialogOpen(false);
                setPendingDeleteGroup(null);
              }}
              disabled={isSaving}
              className='border-primary/35 text-primary hover:bg-primary/10'>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDelete}
              disabled={isSaving}
              variant='destructive'>
              {isSaving ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MobileMenuButton
        isOpen={isMobileMenuOpen}
        onToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
      />

      <Sidebar
        key='persistent-sidebar'
        isMobileMenuOpen={isMobileMenuOpen}
        setIsMobileMenuOpen={setIsMobileMenuOpen}
      />

      <div className='flex-1 flex flex-col min-h-0 overflow-hidden transition-all duration-200 ease-in-out'>
        <PageHeader
          icon={FolderCog}
          title='Manage Tag Groups'
          subtitle='Create and manage groups of parameters by Universal Descriptors.'>
          <PageHeaderActions />
        </PageHeader>

        <div className='flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-4 sm:p-6'>
          {!isLoadingManageableTenants
            && currentTenantId != null
            && !canManageSelectedTenant && (
              <div className='mb-6 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900'>
                You don't have permission to manage tag groups for{" "}
                <span className='font-medium'>
                  {currentTenant?.displayName ?? "the current tenant"}
                </span>
                . Switch tenants in the sidebar to manage tag groups.
              </div>
            )}

          <div className='grid grid-cols-1 xl:grid-cols-2 gap-6'>
            <Card className={fleetOverviewCardClass}>
              <CardHeader className='border-b border-border bg-card px-6 py-4 rounded-t-lg'>
                <CardTitle className='text-lg font-semibold text-foreground'>
                  Existing Groups
                </CardTitle>
                <CardDescription className='text-sm text-muted-foreground'>
                  Select a group to edit or delete.
                </CardDescription>
              </CardHeader>
              <CardContent className='pt-4'>
                <div className='mb-4 relative'>
                  <Input
                    value={groupSearchTerm}
                    onChange={(event) => setGroupSearchTerm(event.target.value)}
                    placeholder='Search groups by name or description...'
                    disabled={isLoadingGroups}
                    className='pr-10'
                  />
                  {groupSearchTerm ? (
                    <button
                      onClick={() => setGroupSearchTerm("")}
                      className='absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground cursor-pointer hover:text-foreground transition-colors z-10'
                      title='Clear search'>
                      <X className='h-4 w-4' />
                    </button>
                  ) : (
                    <Search className={searchStyles.icon} />
                  )}
                </div>

                {isLoadingGroups ? (
                  <LoadingSkeleton className='h-32 w-full rounded-md' />
                ) : filteredSortedGroups.length === 0 ? (
                  <div className='text-sm text-muted-foreground'>
                    {groupSearchTerm.trim()
                      ? "No groups match your search."
                      : "No groups created yet."}
                  </div>
                ) : (
                  <div className='border border-border rounded-md overflow-hidden'>
                    <Table className='w-full'>
                      <TableHeader className={cardStyles.tableHeader}>
                        <TableRow className={cardStyles.tableRow}>
                          <TableHead className={cardStyles.tableHead}>
                            Name
                          </TableHead>
                          <TableHead className={cardStyles.tableHead}>
                            Description
                          </TableHead>
                          <TableHead
                            className={cn(
                              cardStyles.tableHead,
                              "text-right cursor-default"
                            )}>
                            Actions
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredSortedGroups.map((group) => {
                          const groupId = Number(group.TagGroupId);
                          const descriptorCount = Array.isArray(
                            group.UniversalDescriptors
                          )
                            ? group.UniversalDescriptors.length
                            : 0;
                          return (
                            <TableRow
                              key={groupId}
                              className={cardStyles.bodyRow}>
                              <TableCell className={cardStyles.tableCell}>
                                <div className='flex items-center gap-2'>
                                  <span>{group.TagGroupName}</span>
                                  <span className='text-xs text-muted-foreground'>
                                    ({descriptorCount})
                                  </span>
                                </div>
                              </TableCell>
                              <TableCell className={cardStyles.tableCell}>
                                {group.TagDescription || "-"}
                              </TableCell>
                              <TableCell className='py-2 px-4 text-right'>
                                <div className='inline-flex items-center gap-2'>
                                  <Button
                                    variant='outline'
                                    onClick={() => handleEdit(group)}
                                    disabled={
                                      !canManageSelectedTenant || isSaving
                                    }
                                    aria-label='Edit group'
                                    title='Edit group'
                                    className={outlineIconButton}>
                                    <Pencil className='h-3.5 w-3.5' />
                                  </Button>
                                  <Button
                                    variant='outline'
                                    onClick={() =>
                                      handleDeleteFromList(
                                        groupId,
                                        group.TagGroupName
                                      )
                                    }
                                    disabled={
                                      !canManageSelectedTenant || isSaving
                                    }
                                    aria-label='Delete group'
                                    title='Delete group'
                                    className={outlineIconButton}>
                                    <Trash2 className='h-3.5 w-3.5' />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className={fleetOverviewCardClass}>
              <CardHeader className='border-b border-border bg-card px-6 py-4 rounded-t-lg'>
                <CardTitle className='text-lg font-semibold text-foreground'>
                  {formState.groupId ? "Edit Group" : "Create New Group"}
                </CardTitle>
                <CardDescription className='text-sm text-muted-foreground'>
                  Assign one or more Universal Descriptors to each group.
                </CardDescription>
              </CardHeader>
              <CardContent className='space-y-4 pt-4'>
                <div className='space-y-2'>
                  <Label htmlFor='group-name'>Group Name *</Label>
                  <div className='relative'>
                    <Input
                      id='group-name'
                      value={formState.groupName}
                      onChange={(event) =>
                        setFormState((previous) => ({
                          ...previous,
                          groupName: event.target.value,
                        }))
                      }
                      placeholder='Enter group name'
                      disabled={isSaving || !canManageSelectedTenant}
                      className={cn(
                        isDuplicateGroupName && "border-red-500 focus-visible:ring-red-500",
                        isDuplicateGroupName && "pr-9"
                      )}
                    />
                    {isDuplicateGroupName && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <CircleAlert className='absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-red-500 pointer-events-auto cursor-default' />
                        </TooltipTrigger>
                        <TooltipContent side='top'>
                          A group with this name already exists.
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                </div>

                <div className='space-y-2'>
                  <Label htmlFor='group-description'>
                    Description (Optional)
                  </Label>
                  <Input
                    id='group-description'
                    value={formState.description}
                    onChange={(event) =>
                      setFormState((previous) => ({
                        ...previous,
                        description: event.target.value,
                      }))
                    }
                    placeholder='Add a description for this group'
                    disabled={isSaving || !canManageSelectedTenant}
                  />
                  <p className='text-xs text-muted-foreground'>
                    Add a description to help identify this group.
                  </p>
                </div>

                <div className='space-y-2'>
                  <Label>Universal Descriptors *</Label>
                  <div className='relative'>
                    <Input
                      value={descriptorSearchTerm}
                      onChange={(event) =>
                        setDescriptorSearchTerm(event.target.value)
                      }
                      placeholder='Search descriptors by name or UD...'
                      disabled={
                        isLoadingDescriptorCounts
                        || isSaving
                        || !canManageSelectedTenant
                      }
                      className='pr-10'
                    />
                    {descriptorSearchTerm ? (
                      <button
                        onClick={() => setDescriptorSearchTerm("")}
                        className='absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground cursor-pointer hover:text-foreground transition-colors z-10'
                        title='Clear search'>
                        <X className='h-4 w-4' />
                      </button>
                    ) : (
                      <Search className={searchStyles.icon} />
                    )}
                  </div>
                  <div className='border border-border rounded-md max-h-72 overflow-y-auto'>
                    {isLoadingDescriptorCounts ? (
                      <LoadingSkeleton className='h-32 w-full rounded-md' />
                    ) : !canManageSelectedTenant ? (
                      <div className='p-4 text-sm text-muted-foreground'>
                        No descriptors available for this tenant.
                      </div>
                    ) : filteredDescriptorRows.length === 0 ? (
                      <div className='p-4 text-sm text-muted-foreground'>
                        No descriptors found for this tenant.
                      </div>
                    ) : (
                      <div className='divide-y divide-border'>
                        {filteredDescriptorRows.map((descriptor) => {
                          const descriptorId = Number(
                            descriptor.UniversalDescriptorId
                          );
                          const isSelected =
                            formState.descriptorIds.includes(descriptorId);

                          return (
                            <div
                              key={descriptorId}
                              role='button'
                              tabIndex={isSaving ? -1 : 0}
                              className={cn(
                                "w-full text-left p-3 transition-colors cursor-pointer focus:outline-none",
                                cardStyles.bodyRow,
                                "hover:bg-muted focus:bg-muted"
                              )}
                              onClick={() =>
                                !isSaving
                                && canManageSelectedTenant
                                && toggleDescriptorSelection(descriptorId)
                              }
                              onKeyDown={(event) => {
                                if (isSaving || !canManageSelectedTenant) {
                                  return;
                                }

                                if (
                                  event.key === "Enter"
                                  || event.key === " "
                                ) {
                                  event.preventDefault();
                                  toggleDescriptorSelection(descriptorId);
                                }
                              }}>
                              <div className='flex items-center gap-3'>
                                <Checkbox
                                  checked={isSelected}
                                  onChange={() => {}}
                                  readOnly
                                  className='pointer-events-none'
                                />
                                <div className='flex-1 min-w-0'>
                                  <div className='text-sm font-medium text-foreground truncate'>
                                    {descriptor.TagName}
                                  </div>
                                  <div className='text-xs text-muted-foreground'>
                                    UD: {descriptor.UD}
                                  </div>
                                </div>
                                <div className='text-xs text-muted-foreground whitespace-nowrap'>
                                  ({descriptor.TagCount})
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <p className='text-xs text-muted-foreground'>
                    No limit on number of descriptors per group.
                  </p>
                </div>
              </CardContent>
              <CardFooter className='flex justify-end gap-2 border-t border-border bg-card px-6 py-4'>
                <Button
                  onClick={handleSave}
                  disabled={isSaving || !canManageSelectedTenant || isDuplicateGroupName}
                  className={cn(
                    "disabled:opacity-50 disabled:cursor-not-allowed",
                    formState.groupId
                      ? "font-light text-sm text-white bg-green-600 hover:bg-green-700"
                      : steelChromeButton
                  )}>
                  {isSaving ? "Saving..." : formState.groupId ? "Save" : "Add"}
                </Button>
                <Button
                  variant='outline'
                  onClick={resetForm}
                  disabled={isSaving || !canManageSelectedTenant}
                  className='h-9 font-normal text-sm border-border bg-background text-foreground hover:bg-muted shadow-none'>
                  Cancel
                </Button>
                {formState.groupId && (
                  <Button
                    variant='outline'
                    onClick={handleDeleteCurrent}
                    disabled={isSaving || !canManageSelectedTenant}
                    className='font-light text-sm border-red-600 text-red-600 hover:bg-red-50 hover:text-red-700'>
                    Delete
                  </Button>
                )}
              </CardFooter>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
